/* =============================================================================
   MIXIN DE SEO
   -----------------------------------------------------------------------------
   Gestiona el <head> de cada página: title, description, canonical, hreflang,
   Open Graph y datos estructurados.

   Por qué no usamos vue-meta:
   El HTML definitivo que ve Google lo escribe scripts/prerender.js durante el
   build. Aquí solo hace falta que el <head> se actualice cuando alguien navega
   dentro del sitio sin recargar la página. Eso son treinta líneas y nos evita
   añadir otra dependencia a un stack que ya arrastra versiones antiguas.

   Cómo se usa en un componente:

     import seo from '@/mixins/seo';
     export default {
       mixins: [seo],
       computed: {
         seoData() { return contenido.datosServicio(...); }
       }
     }

   El mixin observa la propiedad computada seoData y reacciona sola cuando
   cambia (por ejemplo al pasar de una página comercial a otra).
   ============================================================================= */

var contenido = require('@/content');
var jsonld = require('@/content/jsonld');

var SITE = contenido.SITE;
var IDIOMAS = contenido.IDIOMAS;

/* Crea la etiqueta si no existe, la actualiza si ya está. Nunca duplica. */
function setTag(selector, tag, attrs) {
  var el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute('data-seo', 'true');
    document.head.appendChild(el);
  }
  Object.keys(attrs).forEach(function (k) {
    el.setAttribute(k, attrs[k]);
  });
  return el;
}

function setMetaName(name, content) {
  if (!content) return;
  setTag('meta[name="' + name + '"]', 'meta', { name: name, content: content });
}

function setMetaProperty(property, content) {
  if (!content) return;
  setTag('meta[property="' + property + '"]', 'meta', { property: property, content: content });
}

function limpiar(selector) {
  var previos = document.head.querySelectorAll(selector);
  Array.prototype.forEach.call(previos, function (el) {
    el.parentNode.removeChild(el);
  });
}

module.exports = {
  watch: {
    seoData: {
      immediate: true,
      handler: function (data) {
        if (data && typeof document !== 'undefined') {
          this.$_aplicarSeo(data);
        }
      }
    }
  },

  methods: {
    $_aplicarSeo: function (data) {
      var urlAbsoluta = SITE.dominio + data.url;

      document.title = data.titleSeo;
      document.documentElement.setAttribute('lang', IDIOMAS[data.lang].codigo);

      setMetaName('description', data.description);
      setTag('link[rel="canonical"]', 'link', { rel: 'canonical', href: urlAbsoluta });

      /* hreflang: una etiqueta por idioma, más x-default al castellano */
      limpiar('link[rel="alternate"][data-seo]');

      var alternativas = data.alternativas || {};
      Object.keys(alternativas).forEach(function (lang) {
        var link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', IDIOMAS[lang].hreflang);
        link.setAttribute('href', SITE.dominio + alternativas[lang]);
        link.setAttribute('data-seo', 'true');
        document.head.appendChild(link);
      });

      if (alternativas.es) {
        var xdef = document.createElement('link');
        xdef.setAttribute('rel', 'alternate');
        xdef.setAttribute('hreflang', 'x-default');
        xdef.setAttribute('href', SITE.dominio + alternativas.es);
        xdef.setAttribute('data-seo', 'true');
        document.head.appendChild(xdef);
      }

      /* Open Graph */
      setMetaProperty('og:title', data.titleSeo);
      setMetaProperty('og:description', data.description);
      setMetaProperty('og:url', urlAbsoluta);
      setMetaProperty('og:type', 'website');
      setMetaProperty('og:locale', data.lang === 'ca' ? 'ca_ES' : 'es_ES');

      /* Datos estructurados: fuera los anteriores, dentro los nuevos */
      limpiar('script[type="application/ld+json"][data-seo]');
      jsonld.construir(data).forEach(function (obj) {
        var script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo', 'true');
        script.text = JSON.stringify(obj);
        document.head.appendChild(script);
      });
    }
  }
};
